import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { JobStatus, JobStatusDetail, JobConfig, JobOverview, JobRunHistory } from 'src/app/content/models/jobs'
import { HeartbeatApiService } from 'src/app/api/heartbeat/heartbeat-api.service'

export const DEFAULT_GRACE_PERIOD_SECONDS = 1800

@Injectable({
  providedIn: 'root',
})
export class JobalarmService {
  public doContainWarning = false
  public doContainCritical = false

  constructor(private heartbeatApi: HeartbeatApiService) {}

  async getJobStatusWithDetails(): Promise<Map<JobStatus, JobStatusDetail[]>> {
    this.doContainWarning = false
    this.doContainCritical = false
    var jobStatues: Map<JobStatus, JobStatusDetail[]> = new Map()

    await this.getJobStatusOverview()
      .toPromise()
      .then((result) => {
        result.forEach((item) => {
          this.evaluateJobStatus(item).subscribe((wrapper) => jobStatues.set(wrapper.jobStatus, wrapper.jobDetails))
        })
      })

    return jobStatues
  }

  containsWarning(): boolean {
    return this.doContainWarning
  }

  containsCritical(): boolean {
    return this.doContainCritical
  }

  private evaluateJobStatus(job: JobStatus): Observable<{ jobStatus: JobStatus; jobDetails: any }> {
    if (job.Alive) {
      job.Status = 'success'

      return this.getJobStatusDetails(job.ProjectName, job.JobName, 1).pipe(
        map((jobDetails) => {
          job.LatestEvent = jobDetails[0]
          return { jobStatus: job, jobDetails: [] }
        }),
      )
    } else {
      job.Status = 'warning'
      this.doContainWarning = true

      return this.getJobStatusDetails(job.ProjectName, job.JobName, 5).pipe(
        map((jobDetails) => {
          job.LatestEvent = jobDetails[0]

          if (this.didJobFail(job.LatestEvent)) {
            job.Status = 'critical'
            this.doContainCritical = true

            job.LatestEvent = this.getOldestJobFailEvent(jobDetails) // We want the first time the job failed. So we go through all fails until we see a success.
          }

          return { jobStatus: job, jobDetails: jobDetails }
        }),
      )
    }
  }

  private getOldestJobFailEvent(jobDetails: JobStatusDetail[]): JobStatusDetail {
    var currentOldest = undefined
    for (let item of jobDetails) {
      if (this.didJobFail(item)) {
        currentOldest = item
      }

      if (this.didJobSucceed(item)) {
        break
      }
    }

    return currentOldest
  }

  private didJobFail(job: JobStatusDetail): boolean {
    return job?.LastRunResult?.toLowerCase() == 'failed'
  }

  private didJobSucceed(job: JobStatusDetail): boolean {
    return job?.LastRunResult?.toLowerCase() == 'success'
  }

  private getJobStatusOverview(): Observable<JobStatus[]> {
    return this.heartbeatApi.Job.getStatusOverview()
  }

  private getJobStatusDetails(projectName: string, jobName: string, daysToGoBack: number = 3): Observable<any> {
    return this.heartbeatApi.Job.getStatusDetails(projectName, jobName, daysToGoBack)
  }

  getJobOverview(limit = 10): Observable<JobOverview[]> {
    return this.heartbeatApi.Job.getOverview(limit)
  }

  getJobRunHistory(projectName: string, jobName: string, from: Date, to: Date): Observable<JobRunHistory> {
    return this.heartbeatApi.Job.getRunHistory(projectName, jobName, from, to)
  }

  getAllJobConfigs(): Observable<JobConfig[]> {
    return this.heartbeatApi.Job.getAllConfigs()
  }

  upsertJobConfig(projectName: string, jobName: string, gracePeriodSeconds: number): Observable<any> {
    return this.heartbeatApi.Job.upsertConfig(projectName, jobName, gracePeriodSeconds)
  }

  deleteJobConfig(projectName: string, jobName: string): Observable<any> {
    return this.heartbeatApi.Job.deleteConfig(projectName, jobName)
  }

  deleteJob(projectName: string, jobName: string): Observable<any> {
    return this.heartbeatApi.Job.delete(projectName, jobName)
  }
}
